import { useState, useEffect } from 'react';
import { 
  User, 
  RefreshCw, 
  AlertCircle,
  Lock,
  BadgeCheck
} from 'lucide-react';
import { useAuth } from '@/lib/auth';

export function UserProfileView() {
  const { session } = useAuth();
  const [profileData, setProfileData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch('/api/user/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to load profile');
      const data = await res.json();
      if (data.success) {
        setProfileData(data.data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [session]);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
          <User className="h-5 w-5 text-blue-500" />
          <span>My SOC Identity Profile</span>
        </h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Authenticated credentials and organization role assignment in the Security Operations Center.
        </p>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 text-xs flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="p-16 flex flex-col items-center justify-center text-muted-foreground gap-3 rounded-2xl border border-border bg-card">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
          <span className="text-xs">Loading profile from Supabase...</span>
        </div>
      ) : profileData ? (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-6">
          {/* Avatar card */}
          <div className="flex items-center gap-4 pb-6 border-b border-border">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold text-2xl shadow-md shadow-blue-500/20">
              {profileData.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground flex items-center gap-2">
                <span>{profileData.name}</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-600">
                  <BadgeCheck className="h-3 w-3" />
                  <span>{profileData.status}</span>
                </span>
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">{profileData.email}</p>
            </div>
          </div>

          {/* Identity Fields (Read-Only) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div className="p-3.5 rounded-xl border border-border bg-muted/20">
              <span className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                Full Name
              </span>
              <span className="font-semibold text-foreground">{profileData.name}</span>
            </div>

            <div className="p-3.5 rounded-xl border border-border bg-muted/20">
              <span className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                Email Address
              </span>
              <span className="font-semibold text-foreground font-mono">{profileData.email}</span>
            </div>

            <div className="p-3.5 rounded-xl border border-border bg-muted/20">
              <span className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                Employee ID
              </span>
              <span className="font-semibold text-foreground font-mono">{profileData.employee_id}</span>
            </div>

            <div className="p-3.5 rounded-xl border border-border bg-muted/20">
              <span className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                Department
              </span>
              <span className="font-semibold text-foreground">{profileData.department}</span>
            </div>

            <div className="p-3.5 rounded-xl border border-blue-500/30 bg-blue-500/5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase font-bold text-blue-500">
                  Organization
                </span>
                <Lock className="h-3 w-3 text-muted-foreground" />
              </div>
              <span className="font-semibold text-foreground">{profileData.organization}</span>
              <p className="text-[10px] text-muted-foreground mt-0.5">Read-only for normal users</p>
            </div>

            <div className="p-3.5 rounded-xl border border-purple-500/30 bg-purple-500/5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase font-bold text-purple-500">
                  Role & Permissions
                </span>
                <Lock className="h-3 w-3 text-muted-foreground" />
              </div>
              <span className="font-bold text-foreground">{profileData.role}</span>
              <p className="text-[10px] text-muted-foreground mt-0.5">Assigned by SOC Governance Administrator</p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
